/**
 * SignedDocumentsBanner — Iteration 88.
 *
 * Immer sichtbarer prominenter Kasten oben in der Projekt-Detail-Seite:
 * zeigt jedes signierte Angebot mit direktem Download-Link für sowohl das
 * signierte Angebot-PDF als auch den Liefer-/Leistungsvertrag.
 *
 * Warum als eigener Banner? Die Angebotsliste ist ein Tab (nicht default) —
 * Nutzer suchen ihre unterschriebenen Dokumente aber oft direkt, ohne den
 * richtigen Tab zu wissen. Dieser Kasten steht IMMER da (auf jedem Tab),
 * sobald mindestens ein signiertes Angebot existiert.
 */
import { useState } from "react";
import { toast } from "sonner";
import { FileCheck2, Download, Wrench, Loader2, Check, Gift, X, Pencil, Mail } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

const fmtDT = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// Iter88zq (2026-08-15) — Cache-Buster für PDF-URLs. Object-Storage-URLs
// bleiben nach Regenerierung identisch, der Browser lädt die zwischen-
// gespeicherte Version ohne den Buster. Wir hängen `?v=<timestamp>` an,
// damit der Browser nach jedem Edit/Regenerate frisch nachlädt.
const bustCache = (url, key) => {
  if (!url) return url;
  const v = key ? new Date(key).getTime() : Date.now();
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${v}`;
};

export default function SignedDocumentsBanner({ offers = [], onUpdate, currentUser }) {
  const signedOffers = (offers || []).filter(
    (o) => o.status === "signed" || o.signature?.signed_at
  );
  const [busy, setBusy] = useState(null); // {id, action}
  const [editingSigId, setEditingSigId] = useState(null);
  const [editForm, setEditForm] = useState({ signed_at: "", signer_name: "" });
  const canEditManual = ["admin", "super_admin", "sales_director"].includes(
    (currentUser?.role || "").toLowerCase(),
  );

  if (signedOffers.length === 0) return null;

  const openEdit = (o) => {
    const sig = o.signature || {};
    const iso = sig.signed_at || o.signed_at || "";
    let localVal = "";
    try {
      const d = new Date(iso);
      if (!Number.isNaN(d.getTime())) {
        localVal = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      }
    } catch { /* noop */ }
    setEditForm({ signed_at: localVal, signer_name: sig.signer_name || "" });
    setEditingSigId(o.id);
  };

  const saveEdit = async (offerId) => {
    setBusy({ id: offerId, action: "edit-sig" });
    try {
      const payload = {};
      if (editForm.signer_name.trim().length >= 2) payload.signer_name = editForm.signer_name.trim();
      if (editForm.signed_at) payload.signed_at = new Date(editForm.signed_at).toISOString();
      const { data } = await api.patch(`/offers/${offerId}/manual-sign`, payload);
      toast.success(
        data.pdf_regenerated
          ? "Signatur aktualisiert · PDFs wurden neu generiert"
          : "Signatur aktualisiert",
      );
      setEditingSigId(null);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    } finally { setBusy(null); }
  };

  // Iter88zo — Download eines Evidence-Files aus der DB und lokales Speichern
  // via Blob. Kein direkter Href, weil Base64 im Offer-Dokument liegt (nicht
  // im Object-Storage).
  const downloadEvidence = async (offerId, index, filename) => {
    setBusy({ id: offerId, action: `evidence-${index}` });
    try {
      const { data } = await api.get(`/offers/${offerId}/manual-sign/evidence/${index}`);
      const byteChars = atob(data.content_base64 || "");
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNums)], { type: data.mime || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || filename || "beleg";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const rebuildSigned = async (o) => {
    setBusy({ id: o.id, action: "rebuild" });
    try {
      const r = await api.post(`/offers/${o.id}/rebuild-signed-pdf`);
      toast.success("Signiertes PDF wurde nachgezogen");
      if (r.data?.pdf_storage_path) {
        window.open(bustCache(fileUrl(r.data.pdf_storage_path)), "_blank", "noopener");
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Neu-Erzeugung fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const buildLL = async (o) => {
    setBusy({ id: o.id, action: "ll" });
    try {
      const r = await api.post(`/offers/${o.id}/liefer-leistung-vertrag`);
      toast.success("Liefer- und Leistungsvertrag erzeugt");
      if (r.data?.pdf_storage_path) {
        window.open(bustCache(fileUrl(r.data.pdf_storage_path)), "_blank", "noopener");
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vertrag konnte nicht erzeugt werden");
    } finally { setBusy(null); }
  };

  // Empfehlungsbonus: Sales-Rep gibt frei oder lehnt ab (Iter88za)
  const approveBonus = async (o) => {
    setBusy({ id: o.id, action: "approve-bonus" });
    try {
      await api.post(`/offers/${o.id}/empfehlungsbonus/approve`, {});
      toast.success("Empfehlungsbonus freigegeben — Kunde wird informiert.");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const rejectBonus = async (o) => {
    const reason = window.prompt("Grund der Ablehnung (wird dem Kunden zugeschickt):", "");
    if (!reason || reason.trim().length < 3) return;
    setBusy({ id: o.id, action: "reject-bonus" });
    try {
      await api.post(`/offers/${o.id}/empfehlungsbonus/reject`, { reason: reason.trim() });
      toast.success("Empfehlungsbonus abgelehnt — Kunde wird informiert.");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ablehnung fehlgeschlagen");
    } finally { setBusy(null); }
  };

  return (
    <div
      className="mb-4 rounded-2xl border-2 border-[#00E575]/50 bg-gradient-to-br from-[#00E575]/8 via-[#18181B] to-[#18181B] overflow-hidden"
      data-testid="signed-documents-banner"
    >
      <div className="px-5 py-3 border-b border-[#00E575]/25 bg-[#00E575]/10 flex items-center gap-2">
        <div className="p-1.5 rounded-lg bg-[#00E575]/20">
          <FileCheck2 size={18} className="text-[#00E575]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[15px] font-bold text-[#00E575]">
            Unterschriebene Dokumente
          </div>
          <div className="text-[11.5px] text-zinc-300 font-medium">
            {signedOffers.length === 1
              ? "1 unterschriebenes Angebot in diesem Projekt"
              : `${signedOffers.length} unterschriebene Angebote in diesem Projekt`}
          </div>
        </div>
      </div>

      <div className="divide-y divide-[#00E575]/15">
        {signedOffers.map((o) => (
          <div key={o.id} className="px-5 py-3.5 space-y-2.5" data-testid={`signed-doc-${o.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[13.5px] font-extrabold text-[#00E575]">
                    {o.offer_number}
                  </span>
                  {o.signature?.channel === "public-qr" && (
                    <span className="px-1.5 py-0.5 rounded bg-[#00E575]/20 text-[9.5px] uppercase tracking-wider font-bold text-[#00E575]">QR</span>
                  )}
                  {(o.signature?.channel || "").startsWith("manual-") && (
                    <span className="px-1.5 py-0.5 rounded bg-blue-500/25 text-[9.5px] uppercase tracking-wider font-bold text-blue-300 inline-flex items-center gap-1">
                      <Mail size={10} /> {(o.signature?.source || "manual").replace("_", " ")}
                    </span>
                  )}
                  <span className="text-[12px] text-zinc-300 font-semibold truncate">
                    {o.package_name}
                  </span>
                </div>
                {editingSigId === o.id ? (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-1.5 items-center bg-zinc-800/60 rounded-lg p-2 border border-zinc-700">
                    <input
                      type="text"
                      value={editForm.signer_name}
                      onChange={(e) => setEditForm(f => ({ ...f, signer_name: e.target.value }))}
                      placeholder="Unterzeichner"
                      className="h-9 rounded bg-zinc-900 border border-zinc-700 px-2 text-[12px]"
                      data-testid={`edit-sig-signer-${o.id}`}
                    />
                    <input
                      type="datetime-local"
                      value={editForm.signed_at}
                      onChange={(e) => setEditForm(f => ({ ...f, signed_at: e.target.value }))}
                      max={new Date().toISOString().slice(0, 16)}
                      className="h-9 rounded bg-zinc-900 border border-zinc-700 px-2 text-[12px]"
                      data-testid={`edit-sig-date-${o.id}`}
                    />
                    <button
                      onClick={() => saveEdit(o.id)}
                      disabled={busy?.id === o.id}
                      className="h-9 px-3 rounded bg-emerald-500 hover:bg-emerald-400 text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1"
                      data-testid={`edit-sig-save-${o.id}`}
                    >
                      {busy?.id === o.id ? <Loader2 className="animate-spin" size={12} /> : <Check size={12} />}
                      Speichern
                    </button>
                    <button
                      onClick={() => setEditingSigId(null)}
                      className="h-9 px-3 rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-[12px]"
                    >
                      Abbrechen
                    </button>
                  </div>
                ) : (
                  <div className="mt-1 text-[12px] text-zinc-100 font-medium space-y-0.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Check size={11} className="text-[#00E575]" />
                      Signiert
                      {o.signature?.signer_name && <> von <b>{o.signature.signer_name}</b></>}
                      {(o.signature?.signed_at || o.signed_at) && <> am <b>{fmtDT(o.signature?.signed_at || o.signed_at)}</b></>}
                      {(o.signature?.channel || "").startsWith("manual-") && canEditManual && (
                        <button
                          onClick={() => openEdit(o)}
                          className="ml-1 text-zinc-400 hover:text-blue-300 inline-flex items-center gap-0.5 text-[10.5px] underline decoration-dotted"
                          title="Signaturdatum korrigieren"
                          data-testid={`edit-sig-btn-${o.id}`}
                        >
                          <Pencil size={9} /> bearbeiten
                        </button>
                      )}
                    </div>
                    {(o.signature?.channel || "").startsWith("manual-") && o.signature?.registered_at && (
                      <div className="text-[10.5px] text-zinc-500 pl-4">
                        Erfasst im System: {fmtDT(o.signature.registered_at)}
                        {o.signature.registered_by_name && <> durch {o.signature.registered_by_name}</>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Signiertes Angebot */}
              {o.pdf_storage_path ? (
                <a
                  href={bustCache(fileUrl(o.pdf_storage_path), o.signature?.last_edited_at || o.signature?.signed_at)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`signed-doc-${o.id}-offer-pdf`}
                  className="h-10 rounded-lg bg-[#00E575]/15 hover:bg-[#00E575]/25 border border-[#00E575]/50 text-[#00E575] text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5"
                  title="Angebots-PDF inkl. Kunden-Unterschrift öffnen"
                >
                  <Download size={14} /> Signiertes Angebot (PDF)
                </a>
              ) : (
                <button
                  onClick={() => rebuildSigned(o)}
                  disabled={busy?.id === o.id}
                  data-testid={`signed-doc-${o.id}-rebuild-offer`}
                  className="h-10 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/50 text-amber-300 text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                >
                  {busy?.id === o.id && busy?.action === "rebuild"
                    ? <Loader2 className="animate-spin" size={14} />
                    : <Wrench size={14} />}
                  Signiertes PDF nachziehen
                </button>
              )}
              {/* Liefer-/Leistungsvertrag */}
              {o.liefer_leistung_pdf_path ? (
                <a
                  href={bustCache(fileUrl(o.liefer_leistung_pdf_path), o.signature?.last_edited_at || o.signature?.signed_at)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`signed-doc-${o.id}-ll-pdf`}
                  className="h-10 rounded-lg bg-[#00E575]/15 hover:bg-[#00E575]/25 border border-[#00E575]/50 text-[#00E575] text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5"
                  title="Liefer- und Leistungsvertrag öffnen"
                >
                  <Download size={14} /> Liefer-/Leistungsvertrag
                </a>
              ) : (
                <button
                  onClick={() => buildLL(o)}
                  disabled={busy?.id === o.id}
                  data-testid={`signed-doc-${o.id}-build-ll`}
                  className="h-10 rounded-lg bg-[#00E575]/10 hover:bg-[#00E575]/20 border border-[#00E575]/40 text-[#00E575] text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                >
                  {busy?.id === o.id && busy?.action === "ll"
                    ? <Loader2 className="animate-spin" size={14} />
                    : <FileCheck2 size={14} />}
                  Vertrag erzeugen
                </button>
              )}
            </div>

            {/* Iter88za — Empfehlungsbonus-Panel: erscheint nur wenn Kunde
                den Bonus geltend gemacht hat. Rep kann freigeben oder ablehnen. */}
            {o.empfehlungsbonus?.claimed_by_customer && (
              <div
                className={`mt-1 rounded-lg border p-3 ${
                  o.empfehlungsbonus.approved_by_rep
                    ? "bg-emerald-500/10 border-emerald-500/40"
                    : o.empfehlungsbonus.rejected_at
                    ? "bg-zinc-800/50 border-zinc-700"
                    : "bg-amber-500/10 border-amber-500/50"
                }`}
                data-testid={`signed-doc-${o.id}-bonus-panel`}
              >
                <div className="flex items-start gap-2">
                  <Gift
                    size={16}
                    className={
                      o.empfehlungsbonus.approved_by_rep
                        ? "text-emerald-400 mt-0.5"
                        : o.empfehlungsbonus.rejected_at
                        ? "text-zinc-400 mt-0.5"
                        : "text-amber-400 mt-0.5"
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-bold">
                      {o.empfehlungsbonus.approved_by_rep && (
                        <span className="text-emerald-300">
                          Empfehlungsbonus freigegeben ({Number(o.empfehlungsbonus.amount_eur || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })})
                        </span>
                      )}
                      {o.empfehlungsbonus.rejected_at && (
                        <span className="text-zinc-300">Empfehlungsbonus abgelehnt</span>
                      )}
                      {!o.empfehlungsbonus.approved_by_rep && !o.empfehlungsbonus.rejected_at && (
                        <span className="text-amber-200">
                          Empfehlungsbonus vom Kunden beansprucht — {Number(o.empfehlungsbonus.amount_eur || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                        </span>
                      )}
                    </div>
                    {o.empfehlungsbonus.referrer_name && (
                      <div className="text-[11.5px] text-zinc-300 mt-0.5">
                        Empfohlen durch: <b className="text-zinc-100">{o.empfehlungsbonus.referrer_name}</b>
                      </div>
                    )}
                    {o.empfehlungsbonus.rejected_reason && (
                      <div className="text-[11.5px] text-zinc-400 mt-0.5 italic">
                        Ablehnungs-Grund: „{o.empfehlungsbonus.rejected_reason}&quot;
                      </div>
                    )}
                  </div>
                </div>
                {!o.empfehlungsbonus.approved_by_rep && !o.empfehlungsbonus.rejected_at && (
                  <div className="mt-2.5 flex gap-2">
                    <button
                      onClick={() => approveBonus(o)}
                      disabled={busy?.id === o.id}
                      data-testid={`bonus-approve-${o.id}`}
                      className="flex-1 h-9 rounded-md bg-emerald-500 hover:bg-emerald-400 text-[#09090B] text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                    >
                      {busy?.id === o.id && busy?.action === "approve-bonus"
                        ? <Loader2 className="animate-spin" size={12} />
                        : <Check size={12} />}
                      Freigeben
                    </button>
                    <button
                      onClick={() => rejectBonus(o)}
                      disabled={busy?.id === o.id}
                      data-testid={`bonus-reject-${o.id}`}
                      className="flex-1 h-9 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 border border-zinc-700 disabled:opacity-60"
                    >
                      {busy?.id === o.id && busy?.action === "reject-bonus"
                        ? <Loader2 className="animate-spin" size={12} />
                        : <X size={12} />}
                      Ablehnen
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Iter88zo — Beweismittel-Downloads bei manuell erfassten Signaturen */}
            {(o.signature?.evidence_files || []).length > 0 && (
              <div className="mt-1 rounded-lg border border-blue-500/30 bg-blue-500/5 p-2.5">
                <div className="text-[10.5px] uppercase tracking-wider font-bold text-blue-300 mb-1.5 flex items-center gap-1.5">
                  <Mail size={11} /> Beweismittel Kunden-Zustimmung ({o.signature.evidence_files.length})
                </div>
                <ul className="space-y-1">
                  {o.signature.evidence_files.map((f, i) => (
                    <li key={`${o.id}-ev-${i}`} className="flex items-center gap-2 text-[12px]">
                      <button
                        onClick={() => downloadEvidence(o.id, i, f.filename)}
                        disabled={busy?.id === o.id}
                        className="flex-1 text-left inline-flex items-center gap-1.5 text-zinc-100 hover:text-blue-300 truncate"
                        data-testid={`signed-doc-${o.id}-evidence-${i}`}
                        title={`${f.filename} · ${((f.size_bytes_approx || 0) / 1024).toFixed(0)} KB · ${f.mime}`}
                      >
                        <Download size={12} className="shrink-0" />
                        <span className="truncate">{f.filename}</span>
                      </button>
                      <span className="text-[10.5px] text-zinc-500 shrink-0">
                        {((f.size_bytes_approx || 0) / 1024).toFixed(0)} KB
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
